import { useState, type ReactElement } from 'react';
import {
  clockOn,
  extensionChoices,
  formatHoldMinutes,
  type MapReservation,
  type MapVenue,
} from '@kratispot/shared';
import { Button } from '@/components/ui/button';
import { moneyOf } from '@/lib/format';
import { useTranslation } from '@/lib/i18n';

// The venue's own step, up to its latest hour, priced by the difference. §9
export function ExtendDialog({
  venue,
  booking,
  ratePerHourCents,
  today,
  nowMinutes,
  busy,
  onClose,
  onExtend,
}: {
  venue: MapVenue;
  booking: MapReservation;
  ratePerHourCents: number;
  today: string;
  nowMinutes: number;
  busy: boolean;
  onClose: () => void;
  onExtend: (holdMinutes: number) => void;
}): ReactElement {
  const { t, language } = useTranslation();
  const clock = clockOn(booking.startsOn, today, nowMinutes);
  const choices = extensionChoices([ratePerHourCents], venue.hold, booking.holdMinutes, clock);
  const [minutes, setMinutes] = useState(choices[0]?.minutes ?? booking.holdMinutes);

  return (
    <>
      <button
        type="button"
        aria-label={t('close')}
        onClick={onClose}
        className="fixed inset-0 z-70 cursor-default bg-black/40"
      />
      <div
        role="dialog"
        aria-label={t('extendHold')}
        className="fixed inset-x-4 top-1/4 z-80 mx-auto mt-safe flex max-w-[380px] flex-col gap-3 rounded-2xl border bg-card p-4 shadow-2xl"
      >
        <h3 className="text-[15px] font-bold">{t('extendHold')}</h3>
        <p className="flex justify-between text-[13px]">
          <span className="text-muted-foreground">{t('heldUntil')}</span>
          <b>{formatHoldMinutes(booking.holdMinutes)}</b>
        </p>
        {choices.length === 0 ? (
          <p className="rounded-xl border p-3 text-[13px] text-muted-foreground">
            {t('holdMaxed')}
          </p>
        ) : (
          <label className="flex flex-col gap-1 text-[13px]">
            <span className="font-medium text-muted-foreground">{t('newTime')}</span>
            <select
              className="h-11 w-full rounded-xl border bg-background px-3 text-base"
              value={minutes}
              onChange={(event) => setMinutes(Number(event.target.value))}
            >
              {choices.map((choice) => (
                <option key={choice.minutes} value={choice.minutes}>
                  {formatHoldMinutes(choice.minutes)} · +
                  {moneyOf(choice.feeCents, venue.currency, language)}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            className="flex-1"
            disabled={busy || choices.length === 0}
            onClick={() => onExtend(minutes)}
          >
            {t('extendHold')}
          </Button>
        </div>
      </div>
    </>
  );
}
